/**
 * @file RecorderAgendaPanel.tsx
 * @brief Read the agenda note during the meeting it was written for — web.
 *
 * @details
 * Web parity for the recorder's agenda sheet. `prepNoteId` was write-only here
 * too: carried into the durable recorder's draft so the link could be made
 * after the upload, and never rendered — so the points someone wrote before the
 * meeting were unreachable for the whole of it, with the leave guard standing
 * between them and their notes.
 *
 * An inline disclosure rather than a dialog. The recorder is one narrow column
 * and nothing here competes for the space; a modal over a live recording would
 * cover the timer and the mic state, which are exactly what a person is
 * watching while they glance at the agenda.
 *
 * SEVERAL notes since 30-09-2026 (mobile parity, up to MAX_AGENDA_NOTES):
 * one collapsible card per note, a ✕ in each header to remove it, "Add another agenda"
 * under them. All of them are linked when the upload creates the meeting.
 *
 * READ ONLY, by decision (24-08-2026) — same as mobile. No editing, no ticking,
 * no new points: what is said is already in the transcript, and hand-writing
 * points while they are being discussed would make agenda coverage
 * self-fulfilling. Choosing a note IS allowed; the note already existed, and
 * forgetting to attach it at setup otherwise has no remedy once recording
 * starts.
 *
 * Agendas (DES 12-10-2026): the picker lists agendas only (kind=agenda) and
 * the words say "agenda", not "note". No "Write new agenda" here: that is
 * Setup's, since the agenda is read only during a recording.
 *
 * @references
 * - FRONTEND/src/screens/MeetingIntelligence/RecorderAgendaSheet.tsx (mobile twin)
 * - features/notes/NotePickerSheet.tsx (the picker this hands off to)
 * - docs/product/voice-notes.md
 *
 * @author DES
 * @date 24-08-2026
 */

import { useState, type ReactElement } from "react";
import { ChevronDown, LoaderCircle, Plus, X } from "lucide-react";
import { AgendaFlag } from "@/components/icons/AgendaFlag";

import { NotePickerSheet } from "@/features/notes/NotePickerSheet";
import { useNote } from "@/features/notes/api";
import { notePointLevels, notePoints, noteSummary } from "@/features/notes/schemas";
import { pointLabels } from "@/features/notes/pointLevels";
import { isRTLText } from "@/shared/utils/formatters";
import { cn } from "@/shared/theme/cn";
import { MAX_AGENDA_NOTES } from "./capture/boardroomDraftStore";

/** One agenda note: a disclosure with its summary and numbered points. */
function AgendaNoteCard({
  noteId,
  defaultOpen,
  onRemove,
}: {
  noteId: string;
  defaultOpen: boolean;
  onRemove: () => void;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const note = useNote(noteId);
  const points = note.data ? notePoints(note.data) : [];
  // Sub-points (DES 12-10-2026): lettered and indented under their point.
  const levels = note.data ? notePointLevels(note.data, points) : null;
  const labels = pointLabels(levels, points.length);
  const summary = note.data ? noteSummary(note.data) : "";
  const title = note.data?.title?.trim() || "Agenda";

  return (
    <div className="overflow-hidden rounded-2xl bg-secondary">
      {/* Header: the toggle, then Remove as a small ✕ beside it (30-09-2026,
          DES): a link at the foot of the points read as part of the agenda. */}
      <div className="flex items-center transition-colors duration-200 hover:bg-muted">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex min-w-0 flex-1 items-center gap-2.5 py-3 pl-4 pr-2 text-left"
        >
          {/* DES 08-10-2026: app icon, was ListChecks */}
          <AgendaFlag className="h-5 w-5 shrink-0 text-brand" />
          <span className="min-w-0 flex-1 truncate text-[13px] font-bold text-foreground">
            {title}
          </span>
          {note.isPending ? (
            <LoaderCircle className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
          ) : (
            <span className="shrink-0 rounded-full bg-brand px-2 py-0.5 text-[11px] font-semibold tabular-nums text-white">
              {points.length}
            </span>
          )}
          <ChevronDown
            className={cn(
              "h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 motion-reduce:transition-none",
              open && "rotate-180",
            )}
          />
        </button>
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${title} from this meeting`}
          title="Remove from this meeting"
          className="mr-2 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-background hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>

      {open ? (
        <div className="max-h-72 space-y-3 overflow-y-auto px-4 pb-4">
          {summary ? (
            <p
              dir={isRTLText(summary) ? "rtl" : "ltr"}
              className="text-[13px] leading-6 text-muted-foreground"
            >
              {summary}
            </p>
          ) : null}

          {points.length > 0 ? (
            <ol className="space-y-2">
              {points.map((point, i) => (
                <li key={i} className={cn("flex items-start gap-3", levels?.[i] ? "pl-8" : "")}>
                  <span className="w-5 shrink-0 pt-0.5 text-right font-mono text-xs tabular-nums text-muted-foreground">
                    {labels[i]}.
                  </span>
                  {/* Direction per point, from its own text — an agenda
                      routinely mixes an Urdu point with an English one. */}
                  <span
                    dir={isRTLText(point) ? "rtl" : "ltr"}
                    className="min-w-0 text-[14px] leading-6 text-foreground"
                  >
                    {point}
                  </span>
                </li>
              ))}
            </ol>
          ) : note.isPending ? null : (
            <p className="text-[12px] text-muted-foreground">This agenda has no points yet.</p>
          )}
        </div>
      ) : null}
    </div>
  );
}

export function RecorderAgendaPanel({
  noteIds,
  onChange,
}: {
  noteIds: string[];
  /** The full new list. Nothing is linked until the upload creates a meeting. */
  onChange: (noteIds: string[]) => void;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  // The card for a note added just now opens, so its points show at once.
  const [justAdded, setJustAdded] = useState<string | null>(null);
  const canAdd = noteIds.length < MAX_AGENDA_NOTES;

  // Opens as a menu under whichever button asked for it (30-09-2026).
  const agendaPicker = (anchor: ReactElement) => (
    <NotePickerSheet
      anchor={anchor}
      open={pickerOpen}
      onOpenChange={setPickerOpen}
      selectedId={null}
      excludeIds={noteIds}
      onPick={(picked) => {
        setPickerOpen(false);
        if (!picked || noteIds.includes(picked.id)) return;
        setJustAdded(picked.id);
        onChange([...noteIds, picked.id]);
      }}
    />
  );
  return (
    <div className="w-full space-y-3">
      <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
        <h2 className="text-sm font-semibold tracking-tight text-foreground">Agenda</h2>
        <p className="text-[11px] text-muted-foreground">· What you meant to cover.</p>
      </div>

      {noteIds.map((id) => (
        <AgendaNoteCard
          key={id}
          noteId={id}
          defaultOpen={id === justAdded}
          onRemove={() => onChange(noteIds.filter((n) => n !== id))}
        />
      ))}

      {noteIds.length === 0 ? (
        agendaPicker(
          <button
            type="button"
            className="flex w-full items-center gap-2.5 rounded-2xl border border-dashed border-border px-4 py-3 text-left transition-colors duration-200 hover:bg-secondary"
          >
            {/* DES 08-10-2026: app icon, was ListChecks */}
            <AgendaFlag className="h-5 w-5 shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1">
              <span className="block text-[13px] font-bold text-foreground">Attach an agenda</span>
              <span className="block text-[11px] leading-snug text-muted-foreground">
                An agenda you wrote before this meeting. Afterwards its intelligence reports which
                points were discussed and which were never raised.
              </span>
            </span>
          </button>,
        )
      ) : canAdd ? (
        agendaPicker(
          <button
            type="button"
            className="flex items-center gap-1.5 text-[12px] font-semibold text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
          >
            <Plus className="h-3.5 w-3.5" />
            Add another agenda
          </button>,
        )
      ) : (
        <p className="text-[11px] text-muted-foreground">
          A meeting takes up to {MAX_AGENDA_NOTES} agendas.
        </p>
      )}
    </div>
  );
}
